import { useMemo } from 'react';
import { Formik, Form } from 'formik';
import * as yup from 'yup';

import { EditableUser } from 'api-bible-shared-lib/utils/types';
import { nameValidator, emailValidator } from 'api-bible-shared-lib/utils/validation';
import { InputGroup, InputImage } from './Inputs';
import { Button } from './Button';
import FormHeader from './FormHeader';

import { useAccountUser } from '../contexts/User';

export interface UserFormState {
  user: EditableUser & { oldPassword: string; newPassword: string };
}

const validationSchema = yup.object().shape({
  user: yup.object().shape({
    photo: yup.string(),
    email: emailValidator.clone(),
    name: nameValidator.clone(),
    newPassword: yup.string(), // allow AWS backend to do validation
    oldPassword: yup.string(), // allow AWS backend to do validation
  }),
});

interface UserFormProps {
  handleSubmit: (accountFormData: UserFormState) => Promise<void>;
}

export function UserForm({ handleSubmit }: UserFormProps) {
  const { user } = useAccountUser();

  const initialValues = useMemo<UserFormState | undefined>(() => {
    if (!user) return undefined;

    return {
      user: {
        name: user.name,
        email: user.email,
        photo: user.photo,
        oldPassword: '',
        newPassword: '',
      },
    };
  }, [user]);

  if (!user || !initialValues) return null;

  const onSubmit = async (values: UserFormState, { setSubmitting, setFieldValue }: any) => {
    await handleSubmit({ ...values });

    setFieldValue('user.oldPassword', '');
    setFieldValue('user.newPassword', '');

    setSubmitting(false);
  };

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {({ isSubmitting }) => (
        <Form>
          <FormHeader />
          <div className="mb-8">
            <InputImage name="user.photo" defaultSrc={user.photo} storagePrefix={user.id}>
              <svg className="w-16 h-16" viewBox="0 0 256 256" xmlns="http://www.w3.org/2000/svg">
                <g transform="matrix(10.666666666666666,0,0,10.666666666666666,0,0)">
                  <path
                    d="M6.750 6.000 A5.250 5.250 0 1 0 17.250 6.000 A5.250 5.250 0 1 0 6.750 6.000 Z"
                    fill="none"
                    stroke="#3A83FF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1"
                  />
                  <path
                    d="M2.25,23.25a9.75,9.75,0,0,1,19.5,0"
                    fill="none"
                    stroke="#3A83FF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1"
                  />
                </g>
              </svg>
            </InputImage>
          </div>
          <div className="w-full">
            <div className="mb-8">
              <InputGroup name="user.name" label="Full Name" placeholder="Your full name" />
              <InputGroup name="user.email" label="Email Address" placeholder="Email Address" />
              <InputGroup name="user.oldPassword" label="Old Password" />
              <InputGroup name="user.newPassword" label="New Password" />
            </div>

            <div className="flex justify-end">
              <Button outline disabled={isSubmitting} loading={isSubmitting} type="submit">
                Save Changes
              </Button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}
